import { useEffect, useRef, type KeyboardEvent, type PointerEvent } from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/cn'

export interface CategoryOption<Value extends string> {
  value: Value
  label: string
  icon?: LucideIcon
  disabled?: boolean
  reason?: string
}

export interface CategoryTabsProps<Value extends string> {
  options: readonly CategoryOption<Value>[]
  value: Value
  onChange: (value: Value) => void
  label: string
  tabId: (value: Value) => string
  panelId: string
  className?: string
}

/** Shared category pills with a single keyboard stop and automatic activation. */
export function CategoryTabs<Value extends string>({
  options, value, onChange, label, tabId, panelId, className,
}: CategoryTabsProps<Value>) {
  const refs = useRef(new Map<Value, HTMLButtonElement>())
  const containerRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ pointerId: number; startX: number; scrollLeft: number; moved: boolean } | null>(null)
  const suppressClick = useRef(false)
  const enabled = options.filter((option) => !option.disabled)
  const focusable = enabled.some((option) => option.value === value) ? value : enabled[0]?.value

  useEffect(() => {
    const container = containerRef.current
    const selected = refs.current.get(value)
    if (!container || !selected) return
    const row = container.getBoundingClientRect()
    const pill = selected.getBoundingClientRect()
    const delta = pill.left < row.left ? pill.left - row.left
      : pill.right > row.right ? pill.right - row.right : 0
    if (delta) container.scrollBy({ left: delta })
  }, [value])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    function onWheel(event: WheelEvent) {
      // Horizontal trackpad gestures and browser zoom retain native behavior.
      if (!container || event.deltaX || event.ctrlKey || event.shiftKey) return
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? container.clientWidth : 1)
      const next = Math.max(0, Math.min(container.scrollWidth - container.clientWidth, container.scrollLeft + delta))
      if (next === container.scrollLeft) return
      event.preventDefault()
      container.scrollLeft = next
    }
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [])

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    suppressClick.current = false
    if (event.pointerType !== 'mouse' || event.button !== 0) return
    const container = event.currentTarget
    if (container.scrollWidth <= container.clientWidth) return
    drag.current = { pointerId: event.pointerId, startX: event.clientX, scrollLeft: container.scrollLeft, moved: false }
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const current = drag.current
    if (!current || current.pointerId !== event.pointerId) return
    const distance = event.clientX - current.startX
    if (!current.moved && Math.abs(distance) < 5) return
    current.moved = true
    suppressClick.current = true
    event.currentTarget.setPointerCapture(event.pointerId)
    event.currentTarget.scrollLeft = current.scrollLeft - distance
    event.preventDefault()
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    if (drag.current?.pointerId !== event.pointerId) return
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, current: Value) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !enabled.length) return
    event.preventDefault()
    const index = enabled.findIndex((option) => option.value === current)
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1
      : (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length
    const next = enabled[nextIndex].value
    onChange(next)
    refs.current.get(next)?.focus()
  }

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={() => { drag.current = null }}
      onPointerLeave={() => { if (!drag.current?.moved) drag.current = null }}
      onClickCapture={(event) => {
        if (suppressClick.current && event.detail !== 0) {
          event.preventDefault()
          event.stopPropagation()
          suppressClick.current = false
        }
      }}
      className={cn('scrollbar-none flex w-fit min-w-0 max-w-full select-none flex-nowrap gap-1.5 overflow-x-auto overscroll-x-contain', className)}
    >
      {options.map((option) => {
        const selected = option.value === value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            id={tabId(option.value)}
            aria-selected={selected}
            aria-controls={panelId}
            aria-label={option.disabled && option.reason ? `${option.label} — ${option.reason}` : undefined}
            title={option.disabled ? option.reason : undefined}
            disabled={option.disabled}
            tabIndex={option.value === focusable ? 0 : -1}
            ref={(node) => { if (node) refs.current.set(option.value, node); else refs.current.delete(option.value) }}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, option.value)}
            className={cn(
              'inline-flex h-8 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border px-2 text-xs font-medium transition-colors',
              selected ? 'border-accent/30 bg-accent-soft text-accent' : 'border-border bg-surface-raised text-text-muted hover:bg-surface-hover hover:text-text',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
